// M2 taxes (M2_SPEC §8, DESIGN §6.4): state severance on sales (daily), and the federal corporate income tax with the
// percentage depletion allowance, settled quarterly from the closed monthly P&L. US-domiciled company, worldwide income.
// Rates live in data/m2/rules.js. Nothing is charged before 1905 (severance) / 1909 (corporate excise).
import { dayToDate } from '../../core/time.js';
import { corporateTaxRate, excessProfitsTax, depletionAllowance, severanceRate, IDC_EXPENSED_FROM, DEPRECIATION_YEARS, SEVERANCE } from '../../data/m2/rules.js';
import { fieldDef, yf } from '../defs.js';
import { netWorth } from '../finance.js';
import { notify, spend, fmt$ } from '../util.js';
// M3 econ: post-1946 corporate rates, Korean War excess-profits tax, depletion repeal for majors, foreign tax credit
import { corporateTaxRateM3, excessProfitsTaxM3, depletionM3, M3_FROM, SEVERANCE_M3 } from '../../data/m3/rules.js';
import { foreignLedger, isIndependent, companyUsBbld } from '../m3/fiscal.js';
import { corporateTaxRateM4, m4SeveranceFor } from '../m4/marketFiscal.js'; // M4-B: 21% from 2018, SEVERANCE_M4 (2000+)
const rateAt = (y) => (y >= 2000 ? corporateTaxRateM4(y) : y >= M3_FROM ? corporateTaxRateM3(y) : corporateTaxRate(y)); // M4-B: TCJA 2018
const excessAt = (y) => (y >= M3_FROM ? excessProfitsTaxM3(y) : excessProfitsTax(y));
const depletionAt = (y, o) => (y >= M3_FROM ? depletionM3(y, o) : depletionAllowance(y));
const stepM3 = (s, y) => { if (!s?.length || y < s[0][0]) return null; let v = s[0][1]; for (const [yy, x] of s) if (y >= yy) v = x; return v; };

// Payne-Aldrich corporate excise (1909-1913): 1% of net income above $5,000 a year. The 1913 Revenue Act dropped
// the exemption for corporations.
export const EXCISE_EXEMPTION = { from: 1909, until: 1913.75, perYear: 5000 };
export const NOL_QUARTERS = 8;            // losses carry forward 2 years (simple)
const INCOME_CATS = ['revenue', 'productSales', 'dividends', 'patents', 'recoveries', 'hedging']; // M3 econ: + hedging gains/losses
// Ordinary deductions (cash basis). capex & leases are capitalized (depreciation); fines are not deductible.
const DEDUCT_CATS = ['royalties', 'severance', 'wages', 'fuel', 'consumables', 'transport', 'opex', 'crudePurchases', 'refining', 'interest', 'research', 'security', 'protection', 'insurance', 'licenses', 'other',
  'windfallTax', 'spills', 'charter', 'logistics']; // M3: WPT is deductible; spill cleanup; ops day rates & logistics (hostTax is credited, not deducted)
const IDC_CATS = ['wages', 'fuel', 'consumables'];   // intangible drilling costs: capitalized before 1917

export function taxState(state) {
  state.taxes ??= { quarters: [], nol: [], basis: [], paid: 0, depletionSaved: 0, severancePaid: 0 };
  return state.taxes;
}

// ---- severance (daily, with each sale) --------------------------------------------------------
export function severanceFor(state, fieldId) {
  const s4 = yf(state) >= 2000 ? m4SeveranceFor(state, fieldId) : null; // M4-B: state severance 2000-2029
  if (s4 != null) return s4;
  const rid = fieldDef(fieldId)?.regionId;
  if (rid && yf(state) >= M3_FROM && SEVERANCE_M3[rid]) return stepM3(SEVERANCE_M3[rid], yf(state)) ?? 0; // M3 econ: Alaska ELF severance
  return rid ? severanceRate(rid, yf(state)) : 0;
}

export function chargeSeverance(state, fieldId, gross) {
  if (!(gross > 0)) return 0;
  const rate = severanceFor(state, fieldId);
  if (!(rate > 0)) return 0;
  const amt = gross * rate;
  spend(state, amt, 'severance', false);
  taxState(state).severancePaid += amt;
  return amt;
}

// ---- quarterly income tax ------------------------------------------------------------------------
const quarterKey = (year, q) => `${year}Q${q}`;

// The quarter that just ended when the month rolled on `day` (1 Jan/Apr/Jul/Oct), or null.
export function closedQuarter(day) {
  const { year, month, dom } = dayToDate(day);
  if (dom !== 1 || ![1, 4, 7, 10].includes(month)) return null;
  const q = month === 1 ? 4 : (month - 1) / 3;
  const y = month === 1 ? year - 1 : year;
  return { year: y, q, key: quarterKey(y, q), months: [1, 2, 3].map((i) => `${y}-${String((q - 1) * 3 + i).padStart(2, '0')}`), mid: y + (q - 0.5) / 4 };
}

const sum = (months, cats) => months.reduce((a, m) => a + cats.reduce((b, c) => b + (m[c] ?? 0), 0), 0);

// Pure computation for a set of closed months (exported for tests and the UI estimate).
export function computeQuarterTax({ months, y, nolAvail = 0, depreciation = 0, idcCapitalized = false, netAssets = 0, depletionOpts = {}, foreign = null }) {
  const gross = sum(months, ['revenue']);
  const otherIncome = sum(months, INCOME_CATS) - gross;
  const deductCats = idcCapitalized ? DEDUCT_CATS.filter((c) => !IDC_CATS.includes(c)) : DEDUCT_CATS;
  const deductions = sum(months, deductCats) + depreciation;
  const net = gross + otherIncome - deductions;
  const dep = depletionAt(y, depletionOpts);
  const depletion = Math.max(0, Math.min(dep.rate * Math.max(0, gross) * (dep.fraction ?? 1), dep.netCap * Math.max(0, net)));
  let taxable = Math.max(0, net - depletion);
  const nolUsed = Math.min(nolAvail, taxable);
  taxable -= nolUsed;
  const rate = rateAt(y);
  let exempt = 0;
  if (y >= EXCISE_EXEMPTION.from && y < EXCISE_EXEMPTION.until) exempt = Math.min(taxable, EXCISE_EXEMPTION.perYear / 4);
  let normalTax = rate * (taxable - exempt);
  // M3 econ: foreign tax credit — host income taxes offset US tax on the foreign share of income (no double tax)
  const foreignCredit = foreign && gross > 0 ? Math.min(foreign.hostTax, normalTax * Math.min(1, foreign.foreignGross / gross)) : 0;
  normalTax -= foreignCredit;
  const ex = excessAt(y);
  const excessBase = ex ? Math.max(0, taxable - ex.normalReturn * Math.max(0, netAssets) / 4) : 0;
  const excessTax = ex ? ex.rate * excessBase : 0;
  const tax = Math.max(0, normalTax + excessTax);
  return { gross, otherIncome, deductions, depreciation, net, depletion, nolUsed, taxable, exempt, rate, normalTax, foreignCredit, excessBase, excessTax, tax,
    effRate: net > 0 ? tax / net : 0, depletionSaved: depletion * rate, loss: net < 0 ? -net : 0 };
}

function depreciationNow(state, day) {
  const t = taxState(state);
  const life = DEPRECIATION_YEARS * 365;
  t.basis = t.basis.filter((b) => day - b.day < life);
  return t.basis.reduce((a, b) => a + b.amount / (DEPRECIATION_YEARS * 4), 0);
}

// On the 1st of Jan/Apr/Jul/Oct (after finance.rollMonth closed the month): settle the quarter.
export function tickTaxesQuarterly(state) {
  const cq = closedQuarter(state.day);
  if (!cq || cq.mid < 1901) return null;
  const t = taxState(state);
  if (t.quarters.some((q) => q.key === cq.key)) return null;
  const months = state.finance.history.filter((m) => cq.months.includes(m.month));
  if (!months.length) return null;
  const idcCapitalized = cq.mid < IDC_EXPENSED_FROM;
  // capitalize this quarter's capex (+ leases, + IDC before 1917) into the depreciation basis
  const capital = sum(months, ['capex', 'leases']) + (idcCapitalized ? sum(months, IDC_CATS) : 0);
  if (capital > 0) t.basis.push({ day: state.day, amount: capital });
  const depreciation = depreciationNow(state, state.day);
  // net operating losses: expire after NOL_QUARTERS
  const qIndex = cq.year * 4 + cq.q;
  t.nol = t.nol.filter((n) => qIndex - n.q <= NOL_QUARTERS && n.amount > 0.005);
  const nolAvail = t.nol.reduce((a, n) => a + n.amount, 0);
  const m3 = cq.mid >= M3_FROM ? { depletionOpts: { independent: isIndependent(state), bbld: companyUsBbld(state) }, foreign: foreignLedger(state, cq.months) } : {}; // M3 econ
  const r = computeQuarterTax({ months, y: cq.mid, nolAvail, depreciation, idcCapitalized, netAssets: netWorth(state), ...m3 });
  // consume NOLs oldest first, then record a new loss
  let use = r.nolUsed;
  for (const n of t.nol) { const u = Math.min(use, n.amount); n.amount -= u; use -= u; if (use <= 0) break; }
  t.nol = t.nol.filter((n) => n.amount > 0.005);
  if (r.loss > 0) t.nol.push({ q: qIndex, key: cq.key, amount: r.loss });
  const rec = { key: cq.key, day: state.day, ...round(r) };
  t.quarters.push(rec);
  if (t.quarters.length > 160) t.quarters.shift();
  if (r.tax > 0.005) {
    spend(state, r.tax, 'taxes', false);
    t.paid += r.tax;
    t.depletionSaved += r.depletionSaved;
    state.company.stats.taxesPaid = (state.company.stats.taxesPaid ?? 0) + r.tax;
    const dep = r.depletion > 0 ? ` Depletion allowance sheltered ${fmt$(r.depletion)}.` : '';
    const ex = r.excessTax > 0 ? ` (incl. ${fmt$(r.excessTax)} excess-profits tax)` : '';
    notify(state, 'news', { kind: 'finance', text: `${cq.key} taxes: ${fmt$(r.tax)}${ex} on ${fmt$(r.net)} net income.${dep}` });
  }
  return rec;
}

const round = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, typeof v === 'number' ? Math.round(v * 100) / 100 : v]));

// ---- view ------------------------------------------------------------------------
export function taxView(state) {
  const t = state.taxes ?? { quarters: [], nol: [], basis: [], paid: 0, depletionSaved: 0, severancePaid: 0 };
  const y = yf(state);
  const { year, month } = dayToDate(state.day);
  const ytdQ = t.quarters.filter((q) => q.key.startsWith(String(year)));
  const ytdTax = ytdQ.reduce((a, q) => a + q.tax, 0);
  const ytdNet = ytdQ.reduce((a, q) => a + q.net, 0);
  // estimate for the running quarter from closed months of this quarter + the current month
  const qStart = Math.floor((month - 1) / 3) * 3 + 1;
  const keys = [0, 1, 2].map((i) => `${year}-${String(qStart + i).padStart(2, '0')}`);
  const months = [...state.finance.history.filter((m) => keys.includes(m.month)), ...(state.finance.current ? [state.finance.current] : [])];
  const m3 = y >= M3_FROM ? { depletionOpts: { independent: isIndependent(state), bbld: companyUsBbld(state) }, foreign: foreignLedger(state, keys) } : {}; // M3 econ
  const est = computeQuarterTax({ months, y, nolAvail: t.nol.reduce((a, n) => a + n.amount, 0), depreciation: t.basis.reduce((a, b) => a + b.amount / (DEPRECIATION_YEARS * 4), 0), idcCapitalized: y < IDC_EXPENSED_FROM, netAssets: netWorth(state), ...m3 });
  const ex = excessAt(y);
  return {
    year, rate: rateAt(y), excess: ex, depletion: depletionAt(y, m3.depletionOpts ?? {}), idcExpensed: y >= IDC_EXPENSED_FROM,
    exemptionPerYear: y >= EXCISE_EXEMPTION.from && y < EXCISE_EXEMPTION.until ? EXCISE_EXEMPTION.perYear : 0,
    severanceByRegion: Object.fromEntries(Object.keys(SEVERANCE).map((rid) => [rid, severanceRate(rid, y)])),
    lastQuarter: t.quarters[t.quarters.length - 1] ?? null, quarters: t.quarters.slice(-12),
    ytd: { tax: ytdTax, net: ytdNet, effRate: ytdNet > 0 ? ytdTax / ytdNet : 0 },
    totals: { paid: t.paid, depletionSaved: t.depletionSaved, severancePaid: t.severancePaid },
    nolCarry: t.nol.reduce((a, n) => a + n.amount, 0), estimate: round(est), basis: t.basis.reduce((a, b) => a + b.amount, 0),
    nextQuarterMonth: qStart + 3 > 12 ? 1 : qStart + 3,
  };
}
